import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, MessageSquare, CreditCard, ShieldCheck, Droplet, ShoppingCart, ArrowRight, Play, Calendar } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useBranding } from '../contexts/BrandingContext';

const Home = () => {
  const { branding } = useBranding();
  
  const features = [
    {
      icon: Sparkles,
      title: 'Exclusive Rewards',
      description: 'Earn points with every visit and unlock special treatments',
      link: '/memberships',
      linkType: 'internal'
    },
    {
      icon: MessageSquare,
      title: '24/7 Treatment Advisor',
      description: 'Get personalized skincare recommendations anytime',
      action: 'chatbot',
      linkType: 'action'
    },
    {
      icon: CreditCard,
      title: 'Flexible Memberships',
      description: 'Save on treatments with monthly plans that roll over',
      link: '/memberships',
      linkType: 'internal'
    },
    {
      icon: ShieldCheck,
      title: 'Easy Financing',
      description: 'Flexible payment options to fit your budget',
      link: 'https://www.carecredit.com/apply/',
      linkType: 'external'
    },
    {
      icon: Droplet,
      title: 'Premium Products',
      description: 'Shop professional skincare delivered to your door',
      link: '/treatments',
      linkType: 'internal'
    },
    {
      icon: ShoppingCart,
      title: 'Book Anytime',
      description: 'Schedule appointments and shop treatments 24/7',
      link: '#book-now',
      linkType: 'booking'
    }
  ];

  const handleFeatureClick = (feature) => {
    if (feature.linkType === 'action' && feature.action === 'chatbot') {
      // Trigger the chatbot - find and click the chatbot button
      const chatbotButton = document.querySelector('[data-testid="chatbot-toggle"]');
      if (chatbotButton) {
        chatbotButton.click();
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Your Journey to Radiant Skin Starts Here
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Discover personalized treatments, exclusive memberships, and results that transform how you look and feel.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="#book-now" data-testid="hero-book-appointment">
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Your Appointment
                </a>
              </Button>
              <Button size="lg" variant="outline" className="group" asChild>
                <Link to="/treatments">
                  View Treatments
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for Beautiful Skin
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the future of aesthetic care with our innovative platform
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const CardWrapper = ({ children }) => {
                if (feature.linkType === 'internal') {
                  return <Link to={feature.link} className="block">{children}</Link>;
                } else if (feature.linkType === 'external') {
                  return <a href={feature.link} target="_blank" rel="noopener noreferrer" className="block">{children}</a>;
                } else if (feature.linkType === 'booking') {
                  return <a href={feature.link} className="block">{children}</a>;
                } else if (feature.linkType === 'action') {
                  return <button onClick={() => handleFeatureClick(feature)} className="block w-full text-left">{children}</button>;
                }
                return <div>{children}</div>;
              };

              return (
                <CardWrapper key={index}>
                  <div
                    className="group p-8 rounded-2xl border border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 bg-white cursor-pointer h-full"
                  >
                    <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="h-7 w-7 text-gray-900" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                    <div className="mt-4 text-primary font-medium flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      {feature.linkType === 'booking' ? 'Book Now' : 
                       feature.linkType === 'action' ? 'Chat Now' : 
                       feature.linkType === 'external' ? 'Learn More' : 'View More'}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ background: 'hsl(25, 35%, 75%)' }}></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ background: 'hsl(30, 30%, 88%)' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Skin?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Join thousands who have discovered personalized aesthetic care that fits their lifestyle and budget.
          </p>
          <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100" asChild>
            <a href="#book-now">Schedule Your Consultation</a>
          </Button>
        </div>
      </section>

      {/* Membership Teaser */}
      <section className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-12 text-center shadow-lg">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Save with Membership Plans
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Get monthly credits that roll over, exclusive discounts, and priority booking
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/memberships">
                  View Plans <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/treatments">Browse Treatments</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;